import { supabase } from '../lib/supabase'
import { pickCoverageForAppointment, supportCenterAgendaApi } from './supportCenterAgendaApi'
import {
  attachCaseTriageToAppointmentRows,
  mapApiTriageStatusLabel,
  normalizeTriageStatus,
} from './supportCenterCaseTriage'
import { peopleContactsService } from './peopleContacts'
import {
  parseAgendaCreateCanal,
  type SupportCenterElegibilidadeResult,
  type IaApiPessoaCpf,
  type SupportCenterAgendaCreateApiBody,
} from '../types/supportCenterAgenda'
import { resolveAssistanceFormKind } from '../types/supportCenterSpecialties'
import type {
  SupportCenterAppointmentCreateInput,
  SupportCenterAppointmentListFilters,
  SupportCenterAppointmentUpdateInput,
  SupportCenterAgendaEvent,
  SupportCenterAttendantOption,
  SupportCenterPreBookingInput,
} from '../types/supportCenterAppointments'
import type {
  SupportCenterAppointment,
  SupportCenterAppointmentStatus,
  SupportCenterPeopleRef,
  SupportCenterTriageStatus,
} from '../types/supportCenterTriage'
import { nowTimestampSaoPaulo } from '../utils/dateOnly'

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map(String).map((item) => item.trim()).filter(Boolean)
}

function parseEmails(value: string[] | string): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => item.trim()).filter(Boolean)
  }
  return value
    .split(/[,;\s]+/)
    .map((item) => item.trim())
    .filter(Boolean)
}

/** email_paciente nas rotas /centralamparo: exatamente uma string. */
function requirePatientEmail(value: unknown): string {
  if (Array.isArray(value)) {
    throw new Error('Informe apenas um e-mail do paciente.')
  }
  const parts = String(value ?? '')
    .split(/[,;\s]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
  if (parts.length !== 1) {
    throw new Error(
      parts.length === 0
        ? 'Informe o e-mail do paciente.'
        : 'Informe apenas um e-mail do paciente.'
    )
  }
  return parts[0]
}

function mapPeople(row: unknown): SupportCenterPeopleRef | null {
  if (!row || typeof row !== 'object') return null
  const people = row as Record<string, unknown>
  return {
    id: Number(people.id),
    name: (people.name as string | null) ?? null,
    tax_id_number: (people.tax_id_number as string | null) ?? null,
    captured_birth_date: (people.captured_birth_date as string | null) ?? null,
  }
}

function mapSpecialization(
  row: unknown
): SupportCenterAppointment['specialization'] {
  if (!row || typeof row !== 'object') return null
  const spec = row as Record<string, unknown>
  const id = String(spec.id ?? '')
  if (!id) return null
  return {
    id,
    name: String(spec.name ?? ''),
  }
}

function mapAssistance(row: unknown): SupportCenterAppointment['assistance'] {
  if (!row || typeof row !== 'object') return null
  const assistance = row as Record<string, unknown>
  const id = String(assistance.id ?? '')
  if (!id) return null

  const specificFormRaw = assistance.specific_form
  const specificFormRow = Array.isArray(specificFormRaw) ? specificFormRaw[0] : specificFormRaw
  let specific_form: NonNullable<SupportCenterAppointment['assistance']>['specific_form'] = null
  if (specificFormRow && typeof specificFormRow === 'object') {
    const form = specificFormRow as Record<string, unknown>
    if (!form.deleted_at) {
      const form_key = String(form.form_key ?? '').trim()
      if (form_key) {
        specific_form = {
          form_key,
          name: String(form.name ?? '').trim(),
          table_name: String(form.table_name ?? '').trim(),
          triage_table_name: String(form.triage_table_name ?? '').trim(),
        }
      }
    }
  }

  const specific_form_key =
    specific_form?.form_key ||
    (typeof assistance.specific_form_key === 'string'
      ? assistance.specific_form_key.trim()
      : '') ||
    null

  return {
    id,
    name: String(assistance.name ?? ''),
    code: String(assistance.code ?? ''),
    form_kind: resolveAssistanceFormKind(specific_form_key),
    specific_form_key: specific_form_key || null,
    specific_form,
  }
}

function mapCancelReason(row: unknown): SupportCenterAppointment['cancel_reason'] {
  if (!row || typeof row !== 'object') return null
  const reason = Array.isArray(row) ? row[0] : row
  if (!reason || typeof reason !== 'object') return null
  const item = reason as Record<string, unknown>
  const id = String(item.id ?? '')
  if (!id) return null
  return {
    id,
    name: String(item.name ?? ''),
    description: String(item.description ?? ''),
  }
}

function mapProfessional(row: unknown): SupportCenterAppointment['professional'] {
  if (!row || typeof row !== 'object') return null
  const profile = row as Record<string, unknown>
  return {
    id: String(profile.id ?? ''),
    name: (profile.name as string | null) ?? null,
    email: (profile.email as string | null) ?? null,
  }
}

function normalizeScheduledAt(value: string): string {
  const raw = value.trim().replace(' ', 'T')
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return `${raw}:00`
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(raw)) return raw.slice(0, 19)
  return raw
}

function mapTriageJoin(row: Record<string, unknown>): {
  status: SupportCenterTriageStatus | null
  triage_date: string | null
} {
  const triageRaw = row.triage
  const triage = Array.isArray(triageRaw) ? triageRaw[0] : triageRaw
  if (!triage || typeof triage !== 'object') {
    // Triagem do caso sem objeto embed: se há triage_id, status vem do registro (fallback pendente).
    const triageId = String(row.triage_id ?? '').trim()
    if (triageId) {
      return {
        status:
          normalizeTriageStatus(
            (row as { triage_status?: unknown }).triage_status
          ) ?? 'pendente',
        triage_date: null,
      }
    }
    return { status: null, triage_date: null }
  }
  const t = triage as {
    id?: string
    status?: string
    triage_date?: string | null
    deleted_at?: string | null
  }
  if (t.deleted_at) {
    return { status: null, triage_date: null }
  }
  const hasTriage =
    Boolean(String(t.id ?? '').trim()) || Boolean(String(row.triage_id ?? '').trim())
  const status = normalizeTriageStatus(t.status) ?? (hasTriage ? 'pendente' : null)
  const triageDateRaw = t.triage_date
  const triage_date =
    typeof triageDateRaw === 'string' && triageDateRaw.trim()
      ? triageDateRaw.trim().slice(0, 10)
      : null
  return { status, triage_date }
}

function mapAppointment(row: Record<string, unknown>): SupportCenterAgendaEvent {
  const professionalRaw = row.professional
  const professional = Array.isArray(professionalRaw) ? professionalRaw[0] : professionalRaw
  const holderPeopleRaw = row.holder_people ?? row.holderPeople
  const patientPeopleRaw = row.patient_people ?? row.patientPeople
  const internalEmails = asStringArray(row.internal_emails)
  const mappedProfessional = mapProfessional(professional)
  const emailFromInternal = internalEmails[0] || null
  const phoneContactId = (row.phone_contact_id as string | null) ?? null
  const emailContactIds = asStringArray(row.email_contact_ids)

  const triageJoin = mapTriageJoin(row)
  const phoneContactRaw = row.phone_contact ?? row.phoneContact
  const phoneContact = Array.isArray(phoneContactRaw) ? phoneContactRaw[0] : phoneContactRaw
  const phoneFromJoin =
    phoneContact && typeof phoneContact === 'object'
      ? String((phoneContact as { value?: string }).value ?? '').trim() || null
      : null

  return {
    ...(row as unknown as SupportCenterAgendaEvent),
    id: String(row.id ?? ''),
    holder_cpf: String(row.holder_cpf ?? ''),
    patient_cpf: String(row.patient_cpf ?? ''),
    specialization_id: String(row.specialty_id ?? row.professional_id ?? row.specialization_id ?? ''),
    assistance_id: (row.assistance_id as string | null) ?? null,
    channel: (row.channel as SupportCenterAppointment['channel']) ?? null,
    scheduled_at: String(row.scheduled_at ?? ''),
    status: (row.status as SupportCenterAppointmentStatus) || 'marcado',
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
    deleted_at: (row.deleted_at as string | null) ?? null,
    deleted_by: (row.deleted_by as string | null) ?? null,
    cancel_reason_id: (row.cancel_reason_id as string | null) ?? null,
    cancellation_channel:
      (row.cancellation_channel as SupportCenterAppointment['cancellation_channel']) ?? null,
    cancel_reason: mapCancelReason(row.cancel_reason),
    professional_user_id: (row.professional_user_id as string | null) ?? null,
    outlook_event_id: (row.outlook_event_id as string | null) ?? null,
    internal_emails: internalEmails,
    phone_contact_id: phoneContactId,
    email_contact_ids: emailContactIds,
    client_emails: [],
    call_link: (row.call_link as string | null) ?? null,
    call_link_short: (row.call_link_short as string | null) ?? null,
    patient_phone: phoneFromJoin,
    relationship_to_holder: (row.relationship_to_holder as string | null) ?? null,
    confirmed_at: (row.confirmed_at as string | null) ?? null,
    confirmation_requested_at: (row.confirmation_requested_at as string | null) ?? null,
    attendance_started_at: (row.attendance_started_at as string | null) ?? null,
    attendance_completed_at: (row.attendance_completed_at as string | null) ?? null,
    // triage_id resolvido do caso (support_center_assistance_triages), não coluna do agendamento.
    triage_id: (row.triage_id as string | null) ?? null,
    triage_status: triageJoin.status,
    triage_date: triageJoin.triage_date,
    specialization: mapSpecialization(row.specialization),
    assistance: mapAssistance(row.assistance),
    professional: mappedProfessional
      ? {
          ...mappedProfessional,
          email: mappedProfessional.email || emailFromInternal,
        }
      : emailFromInternal
        ? { id: '', name: null, email: emailFromInternal }
        : null,
    holder_people: mapPeople(holderPeopleRaw),
    patient_people: mapPeople(patientPeopleRaw),
  }
}

async function enrichAppointmentsContacts(
  events: SupportCenterAgendaEvent[]
): Promise<SupportCenterAgendaEvent[]> {
  const ids: string[] = []
  for (const event of events) {
    if (event.phone_contact_id) ids.push(event.phone_contact_id)
    for (const emailId of event.email_contact_ids ?? []) {
      if (emailId) ids.push(emailId)
    }
  }
  const contactsById = await peopleContactsService.getByIds(ids)

  return events.map((event) => {
    const phone =
      (event.phone_contact_id
        ? contactsById.get(event.phone_contact_id)?.value?.trim() || null
        : null) ?? event.patient_phone
    const emails = (event.email_contact_ids ?? [])
      .map((id) => contactsById.get(id)?.value?.trim() || '')
      .filter(Boolean)
    return {
      ...event,
      patient_phone: phone,
      client_emails: emails,
    }
  })
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

type AuthUserInfo = { id: string; name?: string; email?: string }

async function fetchUsersInfoByIds(userIds: string[]): Promise<Map<string, AuthUserInfo>> {
  const unique = [...new Set(userIds.filter(Boolean))]
  const map = new Map<string, AuthUserInfo>()
  if (unique.length === 0) return map

  const { data, error } = await supabase.rpc('get_multiple_users_info', {
    user_ids: unique,
  })
  if (error) throw error

  const rows = Array.isArray(data) ? data : []
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const id = String((row as AuthUserInfo).id ?? '')
    if (!id) continue
    map.set(id, {
      id,
      name: typeof (row as AuthUserInfo).name === 'string' ? (row as AuthUserInfo).name : undefined,
      email:
        typeof (row as AuthUserInfo).email === 'string' ? (row as AuthUserInfo).email : undefined,
    })
  }
  return map
}

async function getAuthUserEmail(userId: string): Promise<string | null> {
  if (!userId) return null
  const { data, error } = await supabase.rpc('get_user_info', { user_id: userId })
  if (error) throw error
  const email = data && typeof data === 'object' ? String((data as { email?: string }).email ?? '') : ''
  return email.trim() || null
}

function attachProfessionalEmails(
  events: SupportCenterAgendaEvent[],
  usersById: Map<string, AuthUserInfo>
): SupportCenterAgendaEvent[] {
  return events.map((event) => {
    const professionalId = event.professional_user_id || event.professional?.id
    if (!professionalId) {
      if (event.professional?.email || event.internal_emails[0]) return event
      return event
    }
    const info = usersById.get(professionalId)
    if (!info) return event
    return {
      ...event,
      professional: {
        id: professionalId,
        name: event.professional?.name ?? info.name ?? null,
        email: info.email?.trim() || event.professional?.email || event.internal_emails[0] || null,
      },
    }
  })
}

async function enrichAppointmentProfessional(
  event: SupportCenterAgendaEvent
): Promise<SupportCenterAgendaEvent> {
  const professionalId = event.professional_user_id || event.professional?.id
  if (!professionalId) return event
  const usersById = await fetchUsersInfoByIds([professionalId])
  return attachProfessionalEmails([event], usersById)[0] ?? event
}

async function mapAndEnrichRows(
  rows: Record<string, unknown>[]
): Promise<SupportCenterAgendaEvent[]> {
  const withCaseTriage = await attachCaseTriageToAppointmentRows(rows)
  const events = await enrichAppointmentsContacts(
    withCaseTriage.map((row) => mapAppointment(row))
  )
  const professionalIds = events
    .map((event) => event.professional_user_id || event.professional?.id || '')
    .filter(Boolean)
  const usersById = await fetchUsersInfoByIds(professionalIds)
  return attachProfessionalEmails(events, usersById)
}

function onlyDigitsCpf(value: string): string {
  return onlyDigits(value)
}

async function resolveProfessionalEmail(input: {
  professional_email?: string
  professional_user_id?: string
}): Promise<string> {
  const fromInput = input.professional_email?.trim()
  if (fromInput) return normalizeEmail(fromInput)
  if (input.professional_user_id) {
    const email = await getAuthUserEmail(input.professional_user_id)
    if (email) return normalizeEmail(email)
  }
  throw new Error('Informe o e-mail do profissional responsável.')
}

async function buildApiCreateBody(input: SupportCenterAppointmentCreateInput) {
  if (!input.specialization_id) {
    throw new Error('Selecione o profissional.')
  }
  if (!input.scheduled_at) {
    throw new Error('Informe data e hora do agendamento.')
  }

  const professionalId = input.professional_user_id?.trim() || ''
  if (!professionalId) {
    throw new Error('Selecione o profissional responsável.')
  }

  const holderCpf = onlyDigitsCpf(input.holder_cpf)
  const patientCpf = input.patient_is_insured ? holderCpf : onlyDigitsCpf(input.patient_cpf)
  const professionalEmail = await resolveProfessionalEmail(input)

  let internalEmails = parseEmails(input.internal_emails).map(normalizeEmail)
  if (!internalEmails.includes(professionalEmail)) {
    internalEmails = [professionalEmail, ...internalEmails]
  }

  const patientEmails = [
    ...new Set(
      (input.patient_emails ?? [])
        .map((email) => normalizeEmail(String(email ?? '')))
        .filter(Boolean)
    ),
  ]
  const emailPaciente = requirePatientEmail(patientEmails[0] ?? '')

  const telefonePaciente = onlyDigits(input.patient_phone ?? '')
  if (telefonePaciente.length < 10) {
    throw new Error('Informe o telefone para contato (DDD + número).')
  }

  const codigoAssistencia = input.assistance_code?.trim() || ''
  if (!codigoAssistencia) {
    throw new Error('Selecione a assistência.')
  }

  const holderDeceased = Boolean(input.holder_deceased)
  let especialidadesElegiveis = input.elegibilidadeEspecialidades ?? []
  if (!holderDeceased && especialidadesElegiveis.length === 0) {
    // Create/reagendar: se o form não trouxe a elegibilidade, consulta de novo pelo CPF.
    const eleg = await supportCenterAgendaApi.getElegibilidade(holderCpf)
    especialidadesElegiveis = eleg.especialidades
  }

  const cobertura = pickCoverageForAppointment(
    especialidadesElegiveis,
    input.specialization_id,
    codigoAssistencia
  )

  let codApolice = cobertura?.codApolice ?? null
  let codConvenio = cobertura?.codConvenio ?? null
  let codFornecedor = cobertura?.codFornecedorAssistencia ?? null
  let codCorretor = cobertura?.codCorretor ?? null
  let codCliente = cobertura?.codCliente ?? null

  if (holderDeceased) {
    codApolice = codApolice ?? 0
    codConvenio = codConvenio ?? 0
    codFornecedor = codFornecedor ?? 0
    codCorretor = codCorretor ?? 0
    codCliente = codCliente ?? 0
  } else if (
    codApolice == null ||
    codConvenio == null ||
    codCorretor == null ||
    codCliente == null
  ) {
    throw new Error(
      'Não foi possível obter apólice e convênio da elegibilidade para esta especialidade. Valide o CPF do segurado novamente.'
    )
  }

  const canal = parseAgendaCreateCanal(input.channel)

  const patientName = input.patient_is_insured
    ? input.holder_name.trim() || input.patient_name.trim()
    : input.patient_name.trim()
  if (!patientName) {
    throw new Error(
      input.patient_is_insured
        ? 'Informe o nome do segurado.'
        : 'Informe o nome do Dependente.'
    )
  }

  const patientBirthDate = (
    input.patient_is_insured
      ? input.holder_birth_date || input.patient_birth_date
      : input.patient_birth_date
  )
    ?.trim()
    .slice(0, 10)
  if (!patientBirthDate || !/^\d{4}-\d{2}-\d{2}$/.test(patientBirthDate)) {
    throw new Error(
      input.patient_is_insured
        ? 'Informe a data de nascimento do segurado.'
        : 'Informe a data de nascimento do Dependente.'
    )
  }

  const body: SupportCenterAgendaCreateApiBody = {
    cpf_titular: holderCpf,
    cpf_paciente: patientCpf,
    nome_paciente: patientName,
    data_nascimento_paciente: patientBirthDate,
    id_profissional: professionalId,
    emails_internos: internalEmails,
    email_paciente: emailPaciente,
    telefone_paciente: telefonePaciente,
    especialidade_id: input.specialization_id,
    codigo_assistencia: codigoAssistencia,
    data_agendada: normalizeScheduledAt(input.scheduled_at),
    cod_apolice: codApolice ?? 0,
    cod_convenio: codConvenio ?? 0,
    // API externa espera número; null da elegibilidade vira 0.
    cod_fornecedor_assistencia: codFornecedor ?? 0,
    cod_corretor: codCorretor ?? 0,
    cod_cliente: codCliente ?? 0,
    canal,
  }

  const relationship = input.patient_is_insured
    ? undefined
    : input.relationship_to_holder?.trim() || undefined
  if (relationship) {
    body.relacionamento_com_titular = relationship
  }

  const preBookingId = input.pre_booking_id?.trim()
  if (preBookingId) {
    body.id_pre_agendamento = preBookingId
  }

  return body
}

async function resolveAssistanceIdByCode(code: string): Promise<string> {
  const trimmed = code.trim()
  if (!trimmed) throw new Error('Selecione a assistência.')
  const { data, error } = await supabase
    .from('support_center_assistances')
    .select('id')
    .eq('code', trimmed)
    .is('deleted_at', null)
    .maybeSingle()
  if (error) throw error
  if (!data?.id) throw new Error('Assistência não encontrada para o pré-agendamento.')
  return String(data.id)
}

function formatSupabaseError(error: unknown, fallback: string): string {
  const maybe = error as { message?: string; code?: string; details?: string; hint?: string }
  const message = (error instanceof Error ? error.message : maybe?.message)?.trim() || ''
  const details = maybe?.details?.trim() || ''
  const code = maybe?.code?.trim() || ''
  const blob = `${message} ${details} ${maybe?.hint ?? ''}`.toLowerCase()

  if (code === '23503' || blob.includes('foreign key') || blob.includes('people')) {
    return 'CPF do segurado/paciente ainda não está em people. Valide o CPF novamente e tente o pré-agendamento.'
  }
  if (code === '23514' || blob.includes('check constraint')) {
    return message || 'Valor inválido para uma regra do banco (CHECK).'
  }
  if (message && details) return `${message} (${details})`
  if (message) return code ? `${message} [${code}]` : message
  if (code) return `${fallback} [${code}]`
  return fallback
}

/** Garante linha em people (FK de holder_cpf/patient_cpf no agendamento). */
async function ensurePeopleForPreBooking(
  cpf: string,
  name?: string | null,
  birthDate?: string | null
): Promise<void> {
  const taxId = onlyDigits(cpf)
  if (taxId.length !== 11) return

  const { data: existing, error: lookupError } = await supabase
    .from('people')
    .select('tax_id_number')
    .eq('tax_id_number', taxId)
    .maybeSingle()
  if (lookupError) {
    throw new Error(formatSupabaseError(lookupError, 'Não foi possível consultar people.'))
  }
  if (existing?.tax_id_number) return

  const payload: Record<string, string | null> = {
    tax_id_number: taxId,
    name: name?.trim() || null,
    captured_birth_date: birthDate?.trim().slice(0, 10) || null,
  }
  const { error: insertError } = await supabase.from('people').insert(payload)
  if (insertError) {
    // Corrida: outra sessão gravou o CPF.
    if (insertError.code === '23505') return
    throw new Error(
      formatSupabaseError(
        insertError,
        'Não foi possível gravar o CPF em people para o pré-agendamento.'
      )
    )
  }
}

const APPOINTMENT_LIST_SELECT = `
  id,
  call_link,
  call_link_short,
  outlook_event_id,
  holder_cpf,
  patient_cpf,
  relationship_to_holder,
  internal_emails,
  phone_contact_id,
  email_contact_ids,
  specialty_id,
  assistance_id,
  channel,
  professional_user_id,
  scheduled_at,
  confirmed_at,
  confirmation_requested_at,
  attendance_started_at,
  attendance_completed_at,
  status,
  created_at,
  updated_at,
  deleted_at,
  deleted_by,
  cancel_reason_id,
  cancellation_channel,
  specialization:support_center_specialties!specialty_id(id, name),
  assistance:support_center_assistances!assistance_id(
    id, name, code, specific_form_key,
    specific_form:support_center_forms!specific_form_key(form_key, name, table_name, triage_table_name, deleted_at)
  ),
  cancel_reason:support_center_cancel_reasons!cancel_reason_id(id, name, description),
  phone_contact:people_contacts!phone_contact_id(id, value, contact_type),
  holder_people:people!holder_cpf(id, name, tax_id_number, captured_birth_date),
  patient_people:people!patient_cpf(id, name, tax_id_number, captured_birth_date)
`

/**
 * Após create/reagendar: se o formulário trouxe mais de um e-mail, garante todos em
 * people_contacts e grava a lista completa em email_contact_ids.
 * O primeiro já vai no POST (email_paciente); ensureContact reaproveita se a API gravou.
 */
async function syncAppointmentEmailContacts(params: {
  appointmentId: string
  patientCpf: string
  emails: string[]
  userId?: string | null
}): Promise<void> {
  const emails = [
    ...new Set(params.emails.map((email) => normalizeEmail(String(email ?? ''))).filter(Boolean)),
  ]
  if (emails.length <= 1) return

  const personTaxId = onlyDigits(params.patientCpf)
  if (personTaxId.length !== 11) {
    throw new Error('CPF do paciente inválido para gravar os e-mails de contato.')
  }

  const emailContactIds: string[] = []
  for (const email of emails) {
    const contact = await peopleContactsService.ensureContact({
      personTaxId,
      contactType: 'email',
      value: email,
      label: 'agenda',
      userId: params.userId,
    })
    if (!emailContactIds.includes(contact.id)) {
      emailContactIds.push(contact.id)
    }
  }

  const { error } = await supabase
    .from('support_center_appointments')
    .update({ email_contact_ids: emailContactIds })
    .eq('id', params.appointmentId)

  if (error) throw error
}

/** SELECT + enrich sem criar triagem (pós-POST create/reagendar). */
async function loadAppointmentReadonly(id: string): Promise<SupportCenterAgendaEvent | null> {
  const { data, error } = await supabase
    .from('support_center_appointments')
    .select(APPOINTMENT_LIST_SELECT)
    .eq('id', id)
    .maybeSingle()

  if (error) throw error
  if (!data) return null
  const [enriched] = await mapAndEnrichRows([data as unknown as Record<string, unknown>])
  let event = enriched
  if (
    event.deleted_at &&
    (event.status === 'marcado' || event.status === 'reagendado')
  ) {
    event = { ...event, status: 'cancelado' }
  }
  return event
}

function applyApiTriageFallback(
  event: SupportCenterAgendaEvent,
  apiAppointment: Record<string, unknown>,
  apiTriage?: { id?: string | null; status?: unknown } | null
): SupportCenterAgendaEvent {
  let next = event
  if (!next.triage_id) {
    const fromAppt = String(apiAppointment.triage_id ?? '').trim()
    const fromTriage = String(apiTriage?.id ?? '').trim()
    const triageId = fromAppt || fromTriage || null
    if (triageId) {
      next = { ...next, triage_id: triageId }
    }
  }
  if (!next.triage_status && apiTriage) {
    const status = mapApiTriageStatusLabel(apiTriage.status)
    if (status) {
      next = { ...next, triage_status: status }
    }
  }
  if (!next.triage_status) {
    const fromAppt = normalizeTriageStatus(
      (apiAppointment as { triage_status?: unknown }).triage_status
    )
    if (fromAppt) {
      next = { ...next, triage_status: fromAppt }
    }
  }
  return next
}

export const supportCenterAppointmentsService = {
  /** Elegibilidade do segurado (PASI + coberturas) via IA-API. */
  async getElegibilidade(cpf: string): Promise<SupportCenterElegibilidadeResult> {
    return supportCenterAgendaApi.getElegibilidade(cpf)
  },

  /**
   * Pessoa por CPF via edge `buscar-pessoa-cpf` (people + BigData).
   * `birthdate` opcional no formato `dd-mm-yyyy` (ex.: menores de idade).
   * Ausente → Error com mensagem "Pessoa não encontrada…".
   */
  async getPessoaCpf(cpf: string, birthdate?: string | null): Promise<IaApiPessoaCpf> {
    const digits = cpf.replace(/\D/g, '').slice(0, 11)
    if (digits.length !== 11) {
      throw new Error('Informe um CPF com 11 dígitos.')
    }

    const edgeBirthdate =
      typeof birthdate === 'string' && birthdate.trim() ? birthdate.trim() : null
    const body: { cpf: string; birthdate?: string } = { cpf: digits }
    if (edgeBirthdate) body.birthdate = edgeBirthdate

    const { data, error } = await supabase.functions.invoke('buscar-pessoa-cpf', {
      body,
    })

    let payload: Record<string, unknown> | null =
      data && typeof data === 'object' && !Array.isArray(data)
        ? (data as Record<string, unknown>)
        : null

    if (!payload && error && typeof error === 'object' && 'context' in error) {
      const ctx = (error as { context?: Response }).context
      if (ctx && typeof ctx.json === 'function') {
        try {
          const parsed = await ctx.json()
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            payload = parsed as Record<string, unknown>
          }
        } catch {
          /* corpo indisponível */
        }
      }
    }

    const edgeError =
      typeof payload?.error === 'string' && payload.error.trim() ? payload.error.trim() : null
    const notFound =
      payload?.found === false ||
      (typeof edgeError === 'string' && /não encontrada/i.test(edgeError))

    if (notFound) {
      throw new Error('Pessoa não encontrada para o CPF informado.')
    }

    if (error && !payload) {
      throw new Error(
        error instanceof Error && error.message.trim()
          ? error.message.trim()
          : 'Não foi possível consultar o CPF.'
      )
    }

    if (!payload || payload.found !== true) {
      throw new Error(edgeError || 'Não foi possível consultar o CPF.')
    }

    const birthRaw = payload.birth_date
    const birth =
      typeof birthRaw === 'string' ? birthRaw.slice(0, 10) || null : null
    const nameRaw = payload.name
    const cpfRaw = payload.cpf

    return {
      name: typeof nameRaw === 'string' ? nameRaw.trim() || null : null,
      tax_id_number:
        typeof cpfRaw === 'string'
          ? cpfRaw.replace(/\D/g, '').slice(0, 11) || digits
          : digits,
      captured_birth_date: birth,
    }
  },

  async listUsersInfoByIds(
    userIds: string[]
  ): Promise<Array<{ id: string; name: string; email: string | null }>> {
    const map = await fetchUsersInfoByIds(userIds)
    return [...map.values()].map((info) => ({
      id: info.id,
      name: (info.name ?? '').trim() || info.id,
      email: info.email?.trim() || null,
    }))
  },

  async listAttendantsForAgenda(
    specializationId?: string | null
  ): Promise<SupportCenterAttendantOption[]> {
    const specId = specializationId?.trim() || ''
    let query = supabase
      .from('support_center_specialty_users')
      .select(`
        user_id,
        profile:profiles!user_id(id, name, is_active)
      `)
      .is('deleted_at', null)

    if (specId) {
      query = query.eq('specialty_id', specId)
    }

    const { data, error } = await query

    if (error) throw error

    const candidates: Array<{ id: string; name: string }> = []
    const seenIds = new Set<string>()
    for (const row of data ?? []) {
      const profileRaw = row.profile
      const profile = Array.isArray(profileRaw) ? profileRaw[0] : profileRaw
      if (!profile || typeof profile !== 'object') continue
      const p = profile as { id?: string; name?: string | null; is_active?: boolean }
      if (!p.id || p.is_active === false || seenIds.has(p.id)) continue
      seenIds.add(p.id)
      candidates.push({ id: p.id, name: (p.name ?? '').trim() })
    }

    const usersById = await fetchUsersInfoByIds(candidates.map((c) => c.id))
    const byEmail = new Map<string, SupportCenterAttendantOption>()
    for (const candidate of candidates) {
      const info = usersById.get(candidate.id)
      const email = info?.email?.trim() || ''
      if (!email) continue
      const key = normalizeEmail(email)
      if (byEmail.has(key)) continue
      byEmail.set(key, {
        id: candidate.id,
        name: candidate.name || info?.name || email,
        email,
      })
    }

    return [...byEmail.values()].sort((a, b) =>
      a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
    )
  },

  async listProfessionalsBySpecialization(
    specializationId: string
  ): Promise<SupportCenterAttendantOption[]> {
    if (!specializationId) return []

    const fromSupabase = async (): Promise<SupportCenterAttendantOption[]> => {
      const { data, error } = await supabase
        .from('support_center_specialty_users')
        .select(`
          user_id,
          profile:profiles!user_id(id, name, is_active)
        `)
        .eq('specialty_id', specializationId)
        .is('deleted_at', null)

      if (error) throw error

      const candidates: Array<{ id: string; name: string }> = []
      for (const row of data ?? []) {
        const profileRaw = row.profile
        const profile = Array.isArray(profileRaw) ? profileRaw[0] : profileRaw
        if (!profile || typeof profile !== 'object') continue
        const p = profile as { id?: string; name?: string | null; is_active?: boolean }
        if (!p.id || p.is_active === false) continue
        candidates.push({ id: p.id, name: (p.name ?? '').trim() })
      }
      const usersById = await fetchUsersInfoByIds(candidates.map((c) => c.id))
      return candidates
        .map((c) => {
          const info = usersById.get(c.id)
          return {
            id: c.id,
            name: c.name || info?.name || c.id,
            email: info?.email?.trim() || '',
          }
        })
        .filter((row) => Boolean(row.email))
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
    }

    try {
      const rows = await supportCenterAgendaApi.listProfissionais(specializationId)
      if (rows.length > 0) {
        return rows
          .map((row) => ({
            id: row.id,
            name: row.name,
            email: row.email,
          }))
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
      }
    } catch {
      // Fallback Supabase
    }

    return fromSupabase()
  },

  /** Lista da semana — Supabase (IA-API não tem GET de agendamentos). */
  async listByRange(filters: SupportCenterAppointmentListFilters): Promise<SupportCenterAgendaEvent[]> {
    const defaultActive = [
      'marcado',
      'reagendado',
      'realizado',
      'nao_realizado',
      'pre_agendado',
    ] as SupportCenterAppointmentStatus[]
    const requested =
      filters.statuses ??
      (filters.include_cancelled
        ? ([...defaultActive, 'cancelado'] as SupportCenterAppointmentStatus[])
        : defaultActive)
    const includeCancelled =
      Boolean(filters.include_cancelled) || requested.includes('cancelado')
    const activeStatuses = requested.filter((s) => s !== 'cancelado')

    let query = supabase
      .from('support_center_appointments')
      .select(APPOINTMENT_LIST_SELECT)
      .gte('scheduled_at', filters.range_start)
      .lte('scheduled_at', filters.range_end)
      .order('scheduled_at', { ascending: true })

    // Cancelamento lógico incompleto: deleted_at + status ainda marcado/reagendado.
    // Não confundir com nao_realizado/realizado, que podem ter deleted_at (ex.: não comparecimento).
    // Evitar `.in('status').or(...)` no PostgREST (AND/parse quebram soft-delete).
    // Com cancelados: busca o range e filtra status no cliente.
    if (!includeCancelled) {
      if (activeStatuses.length > 0) {
        // Sem exigir deleted_at IS NULL: nao_realizado/realizado podem ter deleted_at.
        query = query.in('status', activeStatuses)
      }
    }

    if (filters.specialization_id) {
      query = query.eq('specialty_id', filters.specialization_id)
    }
    if (filters.professional_user_id?.trim()) {
      query = query.eq('professional_user_id', filters.professional_user_id.trim())
    } else if (filters.attendant_email?.trim()) {
      // Legado: e-mail em internal_emails (equipe). Preferir professional_user_id.
      query = query.contains('internal_emails', [normalizeEmail(filters.attendant_email)])
    }

    const { data, error } = await query
    if (error) throw error
    const mapped = await mapAndEnrichRows((data ?? []) as unknown as Record<string, unknown>[])
    const allowed = new Set(requested)
    const openStatuses = new Set<SupportCenterAppointmentStatus>([
      'marcado',
      'reagendado',
      'pre_agendado',
    ])
    const visible = mapped
      .map((event) => {
        // Soft-delete de cancelamento sem status atualizado → trata como cancelado.
        if (event.deleted_at && openStatuses.has(event.status)) {
          return { ...event, status: 'cancelado' as SupportCenterAppointmentStatus }
        }
        return event
      })
      .filter((event) => {
        if (event.status === 'cancelado') {
          return includeCancelled && allowed.has('cancelado')
        }
        return allowed.has(event.status)
      })
    return visible
  },

  /**
   * Agendamentos abertos com atendimento já iniciado
   * (formulários pendentes de envio — filtragem de submitted_at no attend service).
   */
  async listStartedOpen(filters?: {
    professional_user_id?: string | null
    specialization_id?: string | null
  }): Promise<SupportCenterAgendaEvent[]> {
    let query = supabase
      .from('support_center_appointments')
      .select(APPOINTMENT_LIST_SELECT)
      // Sem `reagendado`: esse status é o agendamento antigo substituído.
      .in('status', ['marcado', 'realizado'])
      .not('attendance_started_at', 'is', null)
      .is('deleted_at', null)
      .order('scheduled_at', { ascending: true })

    const professionalUserId = filters?.professional_user_id?.trim()
    if (professionalUserId) {
      query = query.eq('professional_user_id', professionalUserId)
    }
    const specializationId = filters?.specialization_id?.trim()
    if (specializationId) {
      query = query.eq('specialty_id', specializationId)
    }

    const { data, error } = await query
    if (error) throw error
    return mapAndEnrichRows((data ?? []) as unknown as Record<string, unknown>[])
  },

  async getById(id: string): Promise<SupportCenterAgendaEvent | null> {
    return loadAppointmentReadonly(id)
  },

  async create(
    input: SupportCenterAppointmentCreateInput,
    userId?: string | null
  ): Promise<SupportCenterAgendaEvent> {
    const body = await buildApiCreateBody(input)
    const row = await supportCenterAgendaApi.create(body)
    const id = String(row.id ?? '')
    if (id) {
      await syncAppointmentEmailContacts({
        appointmentId: id,
        patientCpf: body.cpf_paciente,
        emails: input.patient_emails ?? [],
        userId,
      })
      const refreshed = await loadAppointmentReadonly(id)
      if (refreshed) return applyApiTriageFallback(refreshed, row)
    }
    const [mapped] = await mapAndEnrichRows([row])
    return applyApiTriageFallback(mapped, row)
  },

  /**
   * Hold de horário: INSERT direto no Supabase (status pre_agendado).
   * Não chama a API — sem Outlook/e-mail/triagem. Confirmação via create + pre_booking_id.
   */
  async createPreBooking(input: SupportCenterPreBookingInput): Promise<SupportCenterAgendaEvent> {
    const holderCpf = onlyDigits(input.holder_cpf)
    const patientCpf = onlyDigits(input.patient_cpf) || holderCpf
    if (holderCpf.length !== 11) {
      throw new Error('Informe o CPF do segurado com 11 dígitos.')
    }
    if (patientCpf.length !== 11) {
      throw new Error('Informe o CPF do paciente com 11 dígitos.')
    }
    const professionalId = input.professional_user_id?.trim() || ''
    if (!professionalId) throw new Error('Selecione o profissional responsável.')
    if (!input.specialization_id?.trim()) throw new Error('Selecione a especialidade.')
    if (!input.scheduled_at?.trim()) throw new Error('Informe data e hora do agendamento.')
    const channel = parseAgendaCreateCanal(input.channel)
    const assistanceCode = input.assistance_code.trim()
    const specialtyId = input.specialization_id.trim()

    const assistanceId = await resolveAssistanceIdByCode(assistanceCode)

    let internalEmails = parseEmails(input.internal_emails ?? []).map((email) =>
      email.trim().toLowerCase()
    )
    const professionalEmail = input.professional_email?.trim().toLowerCase()
    if (professionalEmail && !internalEmails.includes(professionalEmail)) {
      internalEmails = [professionalEmail, ...internalEmails]
    }

    const relationship = input.relationship_to_holder?.trim() || null
    const holderName = input.holder_name?.trim() || null
    const holderBirth = input.holder_birth_date?.trim().slice(0, 10) || null
    const patientName =
      patientCpf === holderCpf
        ? holderName
        : input.patient_name?.trim() || null
    const patientBirth =
      patientCpf === holderCpf
        ? holderBirth
        : input.patient_birth_date?.trim().slice(0, 10) || null

    const holderDeceased = Boolean(input.holder_deceased)
    let especialidadesElegiveis = input.elegibilidadeEspecialidades ?? []
    if (!holderDeceased && especialidadesElegiveis.length === 0) {
      const eleg = await supportCenterAgendaApi.getElegibilidade(holderCpf)
      especialidadesElegiveis = eleg.especialidades
    }

    const cobertura = pickCoverageForAppointment(
      especialidadesElegiveis,
      specialtyId,
      assistanceCode
    )

    let policyCode = cobertura?.codApolice ?? null
    let agreementCode = cobertura?.codConvenio ?? null
    let assistanceProviderCode = cobertura?.codFornecedorAssistencia ?? null
    let brokerCode = cobertura?.codCorretor ?? null
    let clientCode = cobertura?.codCliente ?? null

    if (holderDeceased) {
      policyCode = policyCode ?? 0
      agreementCode = agreementCode ?? 0
      assistanceProviderCode = assistanceProviderCode ?? 0
      brokerCode = brokerCode ?? 0
      clientCode = clientCode ?? 0
    } else if (
      policyCode == null ||
      agreementCode == null ||
      brokerCode == null ||
      clientCode == null
    ) {
      throw new Error(
        'Não foi possível obter apólice, convênio, corretor e cliente da elegibilidade. Valide o CPF do segurado novamente.'
      )
    }

    // FK holder_cpf/patient_cpf → people.tax_id_number
    await ensurePeopleForPreBooking(holderCpf, holderName, holderBirth)
    if (patientCpf !== holderCpf) {
      await ensurePeopleForPreBooking(patientCpf, patientName, patientBirth)
    }

    // Select mínimo no INSERT: embed completo no RETURNING pode gerar 400 no PostgREST.
    const { data, error } = await supabase
      .from('support_center_appointments')
      .insert({
        holder_cpf: holderCpf,
        patient_cpf: patientCpf,
        relationship_to_holder: relationship,
        specialty_id: specialtyId,
        assistance_id: assistanceId,
        professional_user_id: professionalId,
        scheduled_at: input.scheduled_at.trim(),
        channel,
        internal_emails: internalEmails,
        status: 'pre_agendado' satisfies SupportCenterAppointmentStatus,
        // Colunas NOT NULL gravadas pela IA-API no create.
        policy_code: policyCode ?? 0,
        agreement_code: agreementCode ?? 0,
        assistance_provider_code: assistanceProviderCode ?? 0,
        broker_code: brokerCode ?? 0,
        client_code: clientCode ?? 0,
      })
      .select('id')
      .single()

    if (error) {
      throw new Error(formatSupabaseError(error, 'Não foi possível salvar o pré-agendamento.'))
    }
    const id = String(data?.id ?? '').trim()
    if (!id) throw new Error('Pré-agendamento criado sem id de retorno.')

    const loaded = await loadAppointmentReadonly(id)
    if (!loaded) throw new Error('Pré-agendamento salvo, mas não foi possível recarregar.')
    return loaded
  },

  /** Cancela pré-agendamento (soft-delete). Não usa a API de cancelar. */
  async cancelPreBooking(id: string, deletedBy: string): Promise<void> {
    if (!id.trim()) throw new Error('Pré-agendamento inválido.')
    if (!deletedBy.trim()) throw new Error('Usuário responsável pelo cancelamento não identificado.')
    // Banco de appointments não tem deleted_reason (só deleted_at / deleted_by).
    const { data, error } = await supabase
      .from('support_center_appointments')
      .update({
        status: 'cancelado' satisfies SupportCenterAppointmentStatus,
        deleted_at: nowTimestampSaoPaulo(),
        deleted_by: deletedBy.trim(),
      })
      .eq('id', id)
      .eq('status', 'pre_agendado')
      .is('deleted_at', null)
      .select('id')
      .maybeSingle()

    if (error) throw error
    if (!data?.id) {
      throw new Error(
        'Pré-agendamento não encontrado ou já não está ativo. Atualize a agenda e tente novamente.'
      )
    }
  },

  /**
   * Reagendar = POST /agendamentos/{id}/reagendar (IA-API).
   * A API cancela o antigo (status reagendado), cria o novo e reutiliza ou cria
   * a triagem do caso (patient_cpf + assistance_id). Não altera status da triagem.
   */
  async update(
    input: SupportCenterAppointmentUpdateInput,
    deletedBy: string,
    cancelReasonId: string,
    cancellationChannel: 'whatsapp' | 'telefone' | 'videochamada'
  ): Promise<SupportCenterAgendaEvent> {
    const { id, ...createInput } = input
    if (!id) throw new Error('Agendamento inválido para reagendar.')
    if (!deletedBy.trim()) {
      throw new Error('Usuário responsável pelo reagendamento não identificado.')
    }
    const reasonId = cancelReasonId.trim()
    if (!reasonId) throw new Error('Selecione o motivo do cancelamento do agendamento anterior.')
    if (
      cancellationChannel !== 'whatsapp' &&
      cancellationChannel !== 'telefone' &&
      cancellationChannel !== 'videochamada'
    ) {
      throw new Error(
        'Selecione o canal do cancelamento do agendamento anterior (Telefone, WhatsApp ou Videochamada).'
      )
    }

    const createBody = await buildApiCreateBody(createInput)
    const result = await supportCenterAgendaApi.reschedule(id, {
      ...createBody,
      excluido_por: deletedBy.trim(),
      id_motivo_cancelamento: reasonId,
      canal_cancelamento: cancellationChannel,
    })

    const row = result.appointment
    const newId = String(row.id ?? '')
    if (newId) {
      await syncAppointmentEmailContacts({
        appointmentId: newId,
        patientCpf: createBody.cpf_paciente,
        emails: createInput.patient_emails ?? [],
        userId: deletedBy,
      })
      const refreshed = await loadAppointmentReadonly(newId)
      if (refreshed) {
        return applyApiTriageFallback(refreshed, row, result.triage)
      }
    }

    const [mapped] = await mapAndEnrichRows([row])
    return applyApiTriageFallback(mapped, row, result.triage)
  },

  async reschedule(
    id: string,
    scheduledAt: string,
    deletedBy: string,
    cancelReasonId: string,
    cancellationChannel: 'whatsapp' | 'telefone' | 'videochamada'
  ): Promise<SupportCenterAgendaEvent> {
    const current = await this.getById(id)
    if (!current) throw new Error('Agendamento não encontrado.')
    if (
      current.status === 'cancelado' ||
      current.status === 'realizado' ||
      current.status === 'nao_realizado'
    ) {
      throw new Error('Não é possível reagendar este atendimento.')
    }

    const professionalEmail =
      current.professional?.email?.trim() || current.internal_emails[0] || ''
    if (!professionalEmail && !current.professional_user_id) {
      throw new Error('Agendamento sem e-mail do profissional responsável.')
    }

    const eleg = await supportCenterAgendaApi.getElegibilidade(onlyDigits(current.holder_cpf))

    return this.update(
      {
        id,
        holder_cpf: current.holder_cpf,
        holder_name: current.holder_people?.name || `CPF ${current.holder_cpf}`,
        holder_birth_date: current.holder_people?.captured_birth_date ?? null,
        patient_cpf: current.patient_cpf,
        patient_name: current.patient_people?.name || `CPF ${current.patient_cpf}`,
        patient_birth_date: current.patient_people?.captured_birth_date ?? null,
        patient_is_insured: onlyDigits(current.holder_cpf) === onlyDigits(current.patient_cpf),
        relationship_to_holder: current.relationship_to_holder,
        internal_emails: current.internal_emails,
        patient_emails: (current.client_emails ?? [])
          .map((email) => email.trim().toLowerCase())
          .filter(Boolean),
        patient_phone: current.patient_phone,
        contact_owner:
          onlyDigits(current.holder_cpf) === onlyDigits(current.patient_cpf) ? 'holder' : 'patient',
        channel:
          current.channel === 'whatsapp' ||
          current.channel === 'telefone' ||
          current.channel === 'videochamada'
            ? current.channel
            : 'whatsapp',
        specialization_id: current.specialization_id,
        assistance_code: current.assistance?.code?.trim() || '',
        professional_email: professionalEmail || undefined,
        professional_user_id: current.professional_user_id || undefined,
        scheduled_at: scheduledAt,
        elegibilidadeEspecialidades: eleg.especialidades,
      },
      deletedBy,
      cancelReasonId,
      cancellationChannel
    )
  },

  /**
   * Cancela agendamento via IA-API (POST /agendamentos/{id}/cancelar).
   * Só o agendamento muda. A triagem compartilhada do caso permanece como está.
   */
  async cancel(
    id: string,
    cancelReasonId: string,
    userId: string,
    cancellationChannel: 'whatsapp' | 'telefone' | 'videochamada'
  ): Promise<void> {
    const reasonId = cancelReasonId.trim()
    if (!reasonId) throw new Error('Selecione o motivo do cancelamento.')
    if (!userId.trim()) {
      throw new Error('Usuário responsável pelo cancelamento não identificado.')
    }
    if (
      cancellationChannel !== 'whatsapp' &&
      cancellationChannel !== 'telefone' &&
      cancellationChannel !== 'videochamada'
    ) {
      throw new Error('Selecione o canal do cancelamento (Telefone, WhatsApp ou Videochamada).')
    }

    await supportCenterAgendaApi.cancel(id, {
      excluido_por: userId.trim(),
      id_motivo_cancelamento: reasonId,
      status: 'cancelado',
      canal_cancelamento: cancellationChannel,
    })

    // Contrato esperado da IA-API: soft-delete (status=cancelado + deleted_at + cancel_reason_id).
    // Se a API só cancela no Outlook / deixa status antigo / só preenche deleted_at,
    // completa aqui. Se hard-delete (linha some), a agenda não tem como listar.
    // Não sobrescrever status finais (realizado / nao_realizado).
    const { data: row, error: readError } = await supabase
      .from('support_center_appointments')
      .select('id, status, deleted_at, cancel_reason_id, cancellation_channel')
      .eq('id', id)
      .maybeSingle()
    if (readError) throw readError
    if (!row) return
    if (
      row.status === 'cancelado' &&
      row.deleted_at &&
      row.cancel_reason_id &&
      row.cancellation_channel
    ) {
      return
    }
    if (row.status === 'realizado' || row.status === 'nao_realizado') return

    const { error } = await supabase
      .from('support_center_appointments')
      .update({
        status: 'cancelado' satisfies SupportCenterAppointmentStatus,
        deleted_at: row.deleted_at || nowTimestampSaoPaulo(),
        deleted_by: userId.trim() || null,
        cancel_reason_id: reasonId,
        cancellation_channel: cancellationChannel,
      })
      .eq('id', id)
    if (error) throw error
  },

  async markRealizado(id: string, completedAt?: string): Promise<void> {
    const current = await this.getById(id)
    if (!current) throw new Error('Agendamento não encontrado.')

    const now = completedAt?.trim() || nowTimestampSaoPaulo()
    const { error } = await supabase
      .from('support_center_appointments')
      .update({
        status: 'realizado' satisfies SupportCenterAppointmentStatus,
        attendance_completed_at: now,
        ...(current.attendance_started_at ? {} : { attendance_started_at: now }),
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  /**
   * Registra o início do atendimento (botão Iniciar).
   * Não sobrescreve se já houver attendance_started_at.
   */
  async markAttendanceStarted(id: string, startedAt?: string): Promise<void> {
    if (!id.trim()) throw new Error('Agendamento não identificado.')
    const at = startedAt?.trim() || nowTimestampSaoPaulo()
    const { error } = await supabase
      .from('support_center_appointments')
      .update({ attendance_started_at: at })
      .eq('id', id)
      .is('deleted_at', null)
      .is('attendance_started_at', null)
      .in('status', ['marcado', 'reagendado'])

    if (error) throw error
  },

  /**
   * No-show: status → nao_realizado (sem soft-delete).
   */
  async markNaoRealizado(id: string): Promise<void> {
    const { error } = await supabase
      .from('support_center_appointments')
      .update({
        status: 'nao_realizado' satisfies SupportCenterAppointmentStatus,
      })
      .eq('id', id)
      .in('status', ['marcado', 'reagendado'])
      .is('deleted_at', null)

    if (error) throw error
  },

  /**
   * Horários livres via IA-API `/centralamparo/disponibilidade`
   * (grade do profissional − Outlook free/busy − agendamentos).
   * Query: id_profissional + data_inicio/data_fim (dd/mm/aaaa). Horas cheias.
   * Sem `endDayYmd`, consulta só o dia de `dayYmd`.
   */
  async listAvailableSlots(
    professional: { userId: string; email?: string },
    dayYmd: string,
    endDayYmd?: string
  ): Promise<Array<{ horario: string; scheduled_at: string }>> {
    const professionalId = professional.userId?.trim() || ''
    const day = dayYmd.slice(0, 10)
    const endDay = (endDayYmd ?? dayYmd).slice(0, 10)
    if (
      !professionalId ||
      !/^\d{4}-\d{2}-\d{2}$/.test(day) ||
      !/^\d{4}-\d{2}-\d{2}$/.test(endDay)
    ) {
      return []
    }

    const slots = await supportCenterAgendaApi.listDisponibilidade({
      professionalId,
      dataInicio: day,
      dataFim: endDay,
    })

    return slots
      .map((slot) => ({
        horario: slot.horario || normalizeScheduledAt(slot.scheduled_at).slice(11, 16),
        scheduled_at: normalizeScheduledAt(slot.scheduled_at || `${day}T${slot.horario}:00`),
      }))
      .filter((slot) => Boolean(slot.horario && slot.scheduled_at))
      .sort((a, b) => a.horario.localeCompare(b.horario))
  },
}
